<template>
    <div class="page-automation-list">
        <h3 class="title">
            {{ $t("automation.automations") }}
            <a-badge 
                :count="totalAutomationCount" 
                :number-style="{ backgroundColor: hasHiddenAutomations ? '#faad14' : '#52c41a' }"
                :title="hasHiddenAutomations 
                    ? $t('automation.totalAutomationsWithHidden', { total: totalAutomationCount, visible: visibleAutomationCount })
                    : $t('automation.totalAutomations', { count: totalAutomationCount })"
                v-tippy="{ content: hasHiddenAutomations 
                    ? $t('automation.totalAutomationsWithHidden', { total: totalAutomationCount, visible: visibleAutomationCount })
                    : $t('automation.totalAutomations', { count: totalAutomationCount }) }"
            />
            <span class="operated-task-count">{{ $t("automation.operatedTask") }}: {{ totalAutomationUsage }}</span>
        </h3>

        <!-- Info message for paste feature -->
        <div v-if="hasAutomationInClipboard" class="paste-info-message">
            <i class="material-icons">content_paste</i>
            <span v-html="$t('automation.pasteInfoMessage')" /> <!-- <strong>Ctrl+V</strong> elementi olduğu için v-html kullanıldı -->
        </div>

        <div class="table-wrapper">
            <div class="toolbar">
                <div class="left">
                    <a-input-search
                        style="width: 300px; margin: unset !important"
                        v-model="searchText"
                        :placeholder="$t('message.search')"
                        class="search-input"
                        autoComplete="off"
                    />
                    <CommonFilterTool
                        v-model="breakdownFilter"
                        :keep-state-on-query-params="false"
                        :filter-meta="filterMetas"
                        :label-formatter="filterLabelFormatter"
                        @addFilter="addFilter"
                    />
                </div>
                <div class="right">
                    <PermissionController :permissions="['automation.create']">
                        <SupsisButton @click="showCreateCategoryModal = true" class="category-btn">
                            <i class="material-icons">category</i>
                            {{ $t("automation.createCategory") }}
                        </SupsisButton>
                    </PermissionController>
                    <a-dropdown :trigger="['click']">
                        <PermissionController :permissions="['automation.create']">
                            <SupsisButton primary>
                                <i class="material-icons">add</i>
                                {{ $t("message.newAutomation") }}
                                <i class="material-icons" style="font-size: 16px; margin-left: 5px">arrow_drop_down</i>
                            </SupsisButton>
                        </PermissionController>
                        <a-menu slot="overlay" class="automation-dropdown-menu">
                            <a-menu-item
                                v-if="!module"
                                @click="$feature.beforeCreateTimerAutomation(createEmptyTimerAutomation)"
                                :disabled="loading"
                            >
                                <i class="material-icons">timer</i>
                                {{ $t("automation.createTimerAutomation") }}
                            </a-menu-item>
                            <a-menu-item @click="$feature.beforeCreateEventBasedAutomation(showEventAutomationModal)">
                                <i class="material-icons">event</i>
                                {{ $t("automation.createEventBasedAutomation") }}
                            </a-menu-item>
                            <a-menu-item
                                v-if="!module"
                                @click="$feature.beforeCreateWebhookAutomation(showWebhookAutomationModal)"
                            >
                                <i class="material-icons">webhook</i>
                                {{ $t("automation.createWebhookAutomation") }}
                            </a-menu-item>
                        </a-menu>
                    </a-dropdown>
                </div>
            </div>

            <div v-if="selectionMode" class="selection-toolbar">
                <div class="selection-info">
                    <span>{{ $t('automation.selectedAutomations', { count: checkboxOption.selectedRowKeys.length }) }}</span>
                    <SupsisButton @click="exitSelectionMode" ghost>{{ $t("message.cancelText") }}</SupsisButton>
                </div>
                <PermissionController :permissions="['automation.delete']">
                    <SupsisButton
                        danger
                        v-if="checkboxOption.selectedRowKeys.length > 0"
                        @click="deleteMultiAutomations"
                    >
                        <i class="material-icons">delete</i>
                        {{ $t('automation.deleteAutomations', { count: checkboxOption.selectedRowKeys.length }) }}
                    </SupsisButton>
                </PermissionController>
            </div>

            <div class="categories-container">
                <div
                    v-for="category in categorizedAutomations"
                    :key="category.name"
                    class="category-section"
                    :class="{ 'drag-over': dragOverCategory === category.name }"
                    @drop="onDrop($event, category.name)"
                    @dragover="allowDrop($event, category.name)"
                    @dragleave="onDragLeave"
                >
                    <div class="category-header" @click="toggleCategory(category.name)">
                        <div class="category-info">
                            <SupsisButton
                                ghost
                                small
                                @click.stop="toggleCategory(category.name)"
                                class="collapse-toggle"
                            >
                                <i class="material-icons">
                                    {{ categoryExpandStates[category.name] !== false ? "expand_less" : "expand_more" }}
                                </i>
                            </SupsisButton>
                            <i v-if="category.icon" class="material-icons category-icon">{{ category.icon }}</i>
                            <i v-else class="material-icons category-icon">folder</i>
                            <span class="category-name">{{ category.name === GENERAL_CATEGORY ? $t('automation.generalCategory') : category.name }}</span>
                            <span class="automation-count">({{ category.automations.length }})</span>
                        </div>
                        <div class="category-actions">
                            <SupsisButton
                                v-if="category.name !== GENERAL_CATEGORY"
                                ghost
                                icon="edit"
                                small
                                @click.stop="editCategory(category)"
                                v-tippy="{ content: $t('automation.editCategory') }"
                            />
                            <SupsisButton
                                v-if="category.name !== GENERAL_CATEGORY"
                                ghost
                                icon="delete"
                                small
                                danger
                                @click.stop="deleteCategory(category.name)"
                                v-tippy="{ content: $t('automation.deleteCategory') }"
                            />
                            <a-popover
                                v-if="category.automations.length > 0"
                                trigger="click"
                                placement="bottomRight"
                                overlayClassName="category-actions-dropdown"
                                :visible="categoryDropdownStates[category.name]"
                                @visibleChange="
                                    (visible) => {
                                        $set(categoryDropdownStates, category.name, visible);
                                    }
                                "
                            >
                                <template slot="content">
                                    <div class="dropdown-menu-content">
                                        <div class="dropdown-menu-item submenu-item">
                                            <span class="material-icons">drive_file_move</span>
                                            <span>{{ $t("automation.moveAllAutomations") }}</span>
                                            <span class="material-icons submenu-arrow">chevron_left</span>
                                            <div class="submenu-content">
                                                <div
                                                    v-for="targetCategory in availableCategoriesForCategory(
                                                        category.name
                                                    )"
                                                    :key="targetCategory.name"
                                                    class="submenu-item-content"
                                                    @click="
                                                        moveAllAutomationsToCategory(category.name, targetCategory.name)
                                                    "
                                                >
                                                    <i v-if="targetCategory.icon" class="material-icons">{{
                                                        targetCategory.icon
                                                    }}</i>
                                                    <i v-else class="material-icons">folder</i>
                                                    <span>{{ targetCategory.name === GENERAL_CATEGORY ? $t('automation.generalCategory') : targetCategory.name }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <SupsisButton
                                    ghost
                                    icon="more_vert"
                                    small
                                    @click.stop
                                    class="category-more-actions-btn"
                                />
                            </a-popover>
                        </div>
                    </div>

                    <div
                        class="automations-grid"
                        v-show="categoryExpandStates[category.name] !== false"
                        :class="{ collapsed: categoryExpandStates[category.name] === false }"
                    >
                        <div v-if="category.automations.length === 0" class="empty-category-message">
                            <i class="material-icons">drag_handle</i>
                            <span>{{ $t("automation.emptyCategoryMessage") }}</span>
                        </div>
                        <div
                            v-for="automation in category.automations"
                            :key="automation._id"
                            class="automation-card"
                            :class="{
                                dragging: draggedAutomation && draggedAutomation._id === automation._id,
                                'selection-mode': selectionMode,
                                selected: checkboxOption.selectedRowKeys.includes(automation._id),
                            }"
                            :draggable="!selectionMode"
                            @dragstart="onDragStart($event, automation)"
                            @dragend="onDragEnd"
                            @click="selectionMode ? toggleAutomationSelection(automation._id) : null"
                        >
                            <div class="automation-card-header">
                                <div class="automation-selection" v-if="selectionMode" @click.stop>
                                    <a-checkbox
                                        :checked="checkboxOption.selectedRowKeys.includes(automation._id)"
                                        @change="toggleAutomationSelection(automation._id)"
                                    />
                                </div>
                                <a
                                    class="automation-name"
                                    :href="getAutomationHref(automation)"
                                    @click.stop="handleAutomationClick(automation, $event)"
                                >
                                    {{ automation.name }}
                                </a>
                                <div class="automation-actions" @click.stop>
                                    <a-popover
                                        trigger="click"
                                        placement="bottomRight"
                                        overlayClassName="automation-actions-dropdown"
                                        :visible="dropdownStates[automation._id]"
                                        @visibleChange="
                                            (visible) => {
                                                $set(dropdownStates, automation._id, visible);
                                            }
                                        "
                                    >
                                        <template slot="content">
                                            <div class="dropdown-menu-content">
                                                <div
                                                    class="dropdown-menu-item"
                                                    @click="duplicateAutomation(automation)"
                                                >
                                                    <span class="material-icons">content_copy</span>
                                                    <span>{{ $t("automation.copyAutomation") }}</span>
                                                </div>
                                                <div
                                                    class="dropdown-menu-item"
                                                    @click="copyAutomationAsJSON(automation)"
                                                >
                                                    <span class="material-icons">file_copy</span>
                                                    <span>{{ $t("automation.copyAutomationAsJSON") }}</span>
                                                </div>
                                                <div
                                                    class="dropdown-menu-item"
                                                    v-if="automation.options && automation.options.trigger === 'event' && automation.options.event && automation.options.event.type === 'table.record_updated'"
                                                    @click="openTrackFieldsModal(automation)"
                                                >
                                                    <span class="material-icons">tune</span>
                                                    <span>{{ $t('automation.configureTrackFields') }}</span>
                                                </div>
                                                <div
                                                    class="dropdown-menu-item"
                                                    v-if="automation.options && automation.options.trigger === 'event' && automation.options.event && automation.options.event.type === 'message.matched'"
                                                    @click="openMessageMatchedConfigModal(automation)"
                                                >
                                                    <span class="material-icons">filter_alt</span>
                                                    <span>{{ $t('automation.configureMessageFilter') }}</span>
                                                </div>
                                                <div
                                                    class="dropdown-menu-item"
                                                    v-if="automation.options && automation.options.trigger === 'event' && isPreFilterSupported(automation)"
                                                    @click="openPreFilterModal(automation)"
                                                >
                                                    <span class="material-icons">rule</span>
                                                    <span>{{ $t('automation.configurePreFilters') }}</span>
                                                </div>
                                                <div
                                                    class="dropdown-menu-item"
                                                    v-if="$_.get(user, 'role.isRoot') && isExecutionPrivacySupported(automation)"
                                                    @click="openExecutionPrivacyModal(automation)"
                                                >
                                                    <span class="material-icons">admin_panel_settings</span>
                                                    <span>{{ $t('automation.configureExecutionPrivacy') }}</span>
                                                </div>
                                                <div
                                                    class="dropdown-menu-item"
                                                    v-if="$_.get(user, 'role.isRoot')"
                                                    @click="openPermissionSettingsModal(automation)"
                                                >
                                                    <span class="material-icons">lock</span>
                                                    <span>{{ $t('automation.permissions.title') }}</span>
                                                </div>
                                                <div
                                                    class="dropdown-menu-item"
                                                    @click="openDescriptionModal(automation)"
                                                >
                                                    <span class="material-icons">description</span>
                                                    <span>{{ $t('automation.editDescription') }}</span>
                                                </div>
                                                <div
                                                    class="dropdown-menu-item"
                                                    v-if="debugMode && automation.billable !== false"
                                                    @click="makeFreeTier(automation)"
                                                >
                                                    <span class="material-icons">money_off</span>
                                                    <span>{{ $t('automation.makeFreeTier') }}</span>
                                                </div>
                                                <div
                                                    class="dropdown-menu-item"
                                                    v-if="debugMode && automation.billable === false"
                                                    @click="revertFreeTier(automation)"
                                                >
                                                    <span class="material-icons">attach_money</span>
                                                    <span>{{ $t('automation.revertFreeTier') }}</span>
                                                </div>
                                                <div class="dropdown-menu-item submenu-item">
                                                    <span class="material-icons">drive_file_move</span>
                                                    <span>{{ $t("automation.changeCategory") }}</span>
                                                    <span class="material-icons submenu-arrow">chevron_left</span>
                                                    <div class="submenu-content">
                                                        <div
                                                            v-for="categoryOption in availableCategoriesForAutomation(
                                                                automation
                                                            )"
                                                            :key="categoryOption.name"
                                                            class="submenu-item-content"
                                                            @click="
                                                                moveAutomationToCategory(
                                                                    automation,
                                                                    categoryOption.name
                                                                )
                                                            "
                                                        >
                                                            <i v-if="categoryOption.icon" class="material-icons">{{
                                                                categoryOption.icon
                                                            }}</i>
                                                            <i v-else class="material-icons">folder</i>
                                                            <span>{{ categoryOption.name === GENERAL_CATEGORY ? $t('automation.generalCategory') : categoryOption.name }}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <PermissionController :permissions="['automation.delete']">
                                                    <div
                                                        class="dropdown-menu-item"
                                                        @click="enterSelectionMode(automation)"
                                                    >
                                                        <span class="material-icons">delete</span>
                                                        <span>{{ $t("automation.deleteAutomation") }}</span>
                                                    </div>
                                                </PermissionController>
                                            </div>
                                        </template>
                                        <SupsisButton ghost icon="more_vert" small class="more-actions-btn" />
                                    </a-popover>
                                </div>
                            </div>

                            <div class="automation-trigger">
                                <template v-if="automation.options.trigger === 'timer'">
                                    <i class="material-icons">timer</i>
                                    <span>{{ $t("automation.timerAutomation") }}</span>
                                </template>
                                <template v-else-if="automation.options.trigger === 'event'">
                                    <i class="material-icons">event</i>
                                    <span>{{ $t("automation.eventAutomation") }}: {{ automation.options.event.type }}</span>
                                </template>
                                <template v-else-if="automation.options.trigger === 'webhook'">
                                    <i class="material-icons">webhook</i>
                                    <span>{{ $t('automation.webhook') }}</span>
                                </template>
                                <template v-else-if="automation.options.trigger === 'button'">
                                    <i class="material-icons">smart_button</i>
                                    <span>{{ $t("auto.automationButtonTriggerLabel") }}</span>
                                </template>
                                <template v-else>
                                    <i class="material-icons">settings</i>
                                    <span>{{ automation.options.trigger }}</span>
                                </template>
                            </div>

                            <div v-if="automation.description" class="automation-description" v-tippy="{ content: automation.description }">
                                <i class="material-icons">description</i>
                                <span>{{ automation.description }}</span>
                            </div>

                                            <div v-if="automation.billable === false" class="free-tier-badge" v-tippy="{ content: $t('automation.freeTierTooltip') }">
                                <i class="material-icons">star</i>
                                <span>{{ $t('automation.freeTierBadge') }}</span>
                            </div>

                            <div class="automation-meta">
                                <div class="meta-left">
                                    <div class="created-by" v-if="automation.createdBy">
                                        <Avatar
                                            :size="20"
                                            :src="
                                                users[automation.createdBy._id]
                                                    ? users[automation.createdBy._id].gravatar
                                                    : null
                                            "
                                            :username="
                                                users[automation.createdBy._id]
                                                    ? users[automation.createdBy._id].name
                                                    : $t('automation.system')
                                            "
                                        />
                                        <span>{{
                                            users[automation.createdBy._id]
                                                ? users[automation.createdBy._id].name
                                                : $t('automation.system')
                                        }}</span>
                                    </div>
                                    <div class="created-date">
                                        {{ $moment(automation.createdAt).format("DD.MM.YYYY") }}
                                    </div>
                                </div>
                                <div class="meta-right">
                                    <md-switch
                                        class="md-primary publish-switch"
                                        :id="automation._id"
                                        :key="automation._id"
                                        :value="!automation.published"
                                        @change="togglePublishState(automation)"
                                        v-tippy="{ content: automation.published ? $t('automation.publishAutomation') : $t('automation.unpublishAutomation') }"
                                        >
                                        <span class="switch-label">{{
                                            automation.published ? $t("automation.publishAutomation") : $t("automation.unpublishAutomation")
                                        }}</span>
                                    </md-switch>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Category Modals -->
        <Modal2
            v-if="showCreateCategoryModal || showEditCategoryModal"
            @cancel="closeCategoryModal"
            :showFooter="false"
            :title="editingCategory ? $t('automation.editCategory') : $t('automation.createCategory')"
        >
            <div class="automation-category-form">
                <div class="form-group">
                    <label>{{ $t("automation.categoryName") }}</label>
                    <a-input v-model="categoryForm.name" :placeholder="$t('automation.categoryNamePlaceholder')" />
                </div>
                <div class="form-group">
                    <label>{{ $t("modules.icon") }}</label>
                    <MaterialIconPicker v-model="categoryForm.icon" />
                </div>
                <div class="form-actions d-flex gap-5 flex-end">
                    <SupsisButton @click="closeCategoryModal">{{ $t("message.cancelText") }}</SupsisButton>
                    <SupsisButton primary @click="saveCategory">
                        {{ editingCategory ? $t("message.update") : $t("settings.create") }}
                    </SupsisButton>
                </div>
            </div>
        </Modal2>

        <!-- Existing Modals -->
        <Modal2
            v-if="eventAutomationModalVisible"
            :title="$t('automation.createAnEventAutomation')"
            show-title
            :show-footer="false"
            class-name="create-event-automation-modal"
            @cancel="eventAutomationModalVisible = false"
        >
            <EventSelector @save="createEmptyEventAutomation" :default-module="module" />
        </Modal2>
        <Modal2
            v-if="webhookAutomationFormModalVisible"
            :title="$t('automation.chatWebhookAutomation')"
            show-title
            :show-footer="false"
            class-name="event-modal"
            @cancel="webhookAutomationFormModalVisible = false"
        >
            <WebhookAutomationForm @save="createWebhookAutomation" />
        </Modal2>
        <Modal2
            v-if="tableSelectionModalVisible"
            :title="$t('automation.tableSelection')"
            show-title
            :show-footer="false"
            class-name="table-selection-modal"
            @cancel="tableSelectionModalVisible = false"
        >
            <div class="table-selection-form">
                <div class="event-description" v-if="$event">
                    <template v-if="$event.event === 'table.record_created'">
                        {{ $t("automation.whichTableAutomationAddRecords") }}
                    </template>
                    <template v-else-if="$event.event === 'table.record_updated'">
                        {{ $t("automation.whichTableAutomationUpdateRecords") }}
                    </template>
                    <template v-else-if="$event.event === 'table.record_deleted'">
                        {{ $t("automation.whichTableAutomationDeletedRecords") }}
                    </template>
                    <template v-else-if="$event.event === 'table.record_automation_button_clicked'">
                        {{ $t("automation.whichTableAutomationClickRecords") }}
                    </template>
                </div>
                <CustomPicker
                    v-model="selectedTableId"
                    :items="tables"
                    idKey="_id"
                    labelKey="name"
                    :title="$t('automation.selectTable')"
                    icon="table_chart"
                />
                <div v-if="selectedTableId && $event && $event.event === 'table.record_updated'" class="form-group">
                    <label>{{ $t('automation.trackFieldsLabel') }}</label>
                    <a-select
                        mode="multiple"
                        :loading="tableFieldLoading"
                        v-model="selectedFieldIds"
                        :placeholder="$t('automation.trackFieldsSelectPlaceholder')"
                        style="width:100%"
                    >
                        <a-select-option v-for="f in tableFields" :key="f._id" :value="f._id">{{ f.name }}</a-select-option>
                    </a-select>
                </div>
                <PermissionController :permissions="['automation.create', 'table.create']" controller="every">
                    <SupsisButton primary @click="createEventAutomationWithTable"
                        >{{ $t("tables.create") }}
                    </SupsisButton>
                </PermissionController>
            </div>
        </Modal2>
        <Modal2
            v-if="trackFieldModalVisible"
            :title="$t('automation.trackFieldsLabel')"
            show-title
            :show-footer="false"
            class-name="table-selection-modal"
            @cancel="closeTrackFieldsModal"
        >
            <div class="table-selection-form">
                <div class="form-group">
                    <label>{{ $t('automation.trackFieldsLabel') }}</label>
                    <a-select
                        mode="multiple"
                        :loading="trackFieldLoading"
                        v-model="trackSelectedFieldIds"
                        :placeholder="$t('automation.selectFieldPlaceholder')"
                        style="width:100%"
                    >
                        <a-select-option v-for="f in trackTableFields" :key="f._id" :value="f._id">{{ f.name }}</a-select-option>
                    </a-select>
                </div>
                <div class="form-actions d-flex gap-5 flex-end">
                    <SupsisButton @click="closeTrackFieldsModal">{{ $t('message.cancelText') }}</SupsisButton>
                    <SupsisButton primary @click="saveTrackFieldsForAutomation">{{ $t('message.save') }}</SupsisButton>
                </div>
            </div>
        </Modal2>

        <!-- Message Matched Configuration Modal -->
        <MessageMatchedConfig
            :visible="messageMatchedConfigModalVisible"
            v-model="messageMatchedConfig"
            :show-footer="false"
            :ok-text="$t('message.save')"
            :cancel-text="$t('message.cancelText')"
            @save="saveMessageMatchedConfig"
            @cancel="closeMessageMatchedConfigModal"
        />

        <!-- PreFilter Configuration Modal -->
        <Modal2
            v-if="preFilterModalVisible"
            :title="$t('automation.preFiltersConfiguration')"
            show-title
            :show-footer="false"
            class-name="prefilter-config-modal"
            @cancel="closePreFilterModal"
        >
            <div class="prefilter-modal-content">
                <PreFilterConfig v-model="preFilterConfig" :automation="preFilterAutomation" />
                <div class="form-actions d-flex gap-5 flex-end" style="margin-top: 20px">
                    <SupsisButton @click="closePreFilterModal">{{ $t("message.cancelText") }}</SupsisButton>
                    <SupsisButton primary @click="savePreFilterConfig">{{ $t("message.save") }}</SupsisButton>
                </div>
            </div>
        </Modal2>

        <!-- Execution Privacy Configuration Modal -->
        <Modal2
            v-if="executionPrivacyModalVisible"
            :title="$t('automation.executionPrivacyConfiguration')"
            show-title
            :show-footer="false"
            class-name="execution-privacy-config-modal"
            @cancel="closeExecutionPrivacyModal"
        >
            <div class="execution-privacy-modal-content">
                <ExecutionPrivacyChanger v-model="executionPrivacyConfig" />
                <div class="form-actions d-flex gap-5 flex-end" style="margin-top: 20px">
                    <SupsisButton @click="closeExecutionPrivacyModal">{{ $t("message.cancelText") }}</SupsisButton>
                    <SupsisButton primary @click="saveExecutionPrivacyConfig">{{ $t("message.save") }}</SupsisButton>
                </div>
            </div>
        </Modal2>

        <!-- Permission Settings Modal -->
        <Modal2
            v-if="permissionSettingsModalVisible"
            :title="$t('automation.permissions.title')"
            show-title
            :show-footer="false"
            class-name="permission-settings-modal"
            @cancel="closePermissionSettingsModal"
            :width="1400"
        >
            <AutomationPermissionSettings 
                v-if="permissionSettingsAutomation"
                :automation="permissionSettingsAutomation"
                @close="closePermissionSettingsModal"
                @saved="onPermissionConfigSaved"
            />
        </Modal2>

        <!-- Description Edit Modal -->
        <Modal2
            v-if="descriptionModalVisible"
            :title="$t('automation.editDescription')"
            show-title
            :show-footer="false"
            class-name="description-modal"
            @cancel="closeDescriptionModal"
        >
            <div class="description-modal-content">
                <a-textarea
                    v-model="descriptionForm.description"
                    :placeholder="$t('automation.descriptionPlaceholder')"
                    :auto-size="{ minRows: 3, maxRows: 8 }"
                />
                <div class="form-actions d-flex gap-5 flex-end" style="margin-top: 20px">
                    <SupsisButton @click="closeDescriptionModal">{{ $t("message.cancelText") }}</SupsisButton>
                    <SupsisButton primary @click="saveDescription">{{ $t("message.save") }}</SupsisButton>
                </div>
            </div>
        </Modal2>
    </div>
</template>
<script>
import Vue from "vue";
import CommonFilterTool from "../../../Chatbot/pages/archive/CommonFilterTool.vue";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import NoMessage from "../../../TeamChat/NoMessage.vue";
import log2 from "../../../../../utils/log2";
import moment from "moment/moment";
import { AUTOMATION_API } from "../../../../../api/automation";
import { TABLE_API } from "../../../../../api/table";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import Modal2 from "../../../../../components/common/Modal2.vue";
import EventSelector from "../../components/EventSelector.vue";
import WebhookAutomationForm from "../../components/WebhookAutomationForm.vue";
import { mapState } from "vuex";
import CustomPicker from "../../../../../components/pickers/CustomPicker.vue";
import Avatar from "vue-avatar";
import PermissionController from "../../../../../components/permission-controller/PermissionController";
import MaterialIconPicker from "../../../Module/MaterialIconPicker.vue";
import PreFilterConfig from "../../components/PreFilterConfig.vue";
import MessageMatchedConfig from "../../components/MessageMatchedConfig.vue";
import ExecutionPrivacyChanger from "../../../../../components/automation/ExecutionPrivacyChanger.vue";
import AutomationPermissionSettings from "../../components/AutomationPermissionSettings.vue";
import { canViewAutomation } from "../../../../../utils/AutomationPermissionUtils";
import _ from "lodash";

const log = log2("AutomationList");
const GENERAL_CATEGORY = "general";
export default Vue.extend({
    name: "AutomationList",
    components: {
        PermissionController,
        WebhookAutomationForm,
        EventSelector,
        Modal2,
        SupsisButton,
        NoMessage,
        SupsisTable,
        CommonFilterTool,
        CustomPicker,
        Avatar,
        MaterialIconPicker,
        PreFilterConfig,
        MessageMatchedConfig,
        ExecutionPrivacyChanger,
        AutomationPermissionSettings,
    },
    props: ["type", "module", "event"],
    data() {
        return {
            GENERAL_CATEGORY,
            searchText: null,
            categories: [], // Separate array to track created categories
            pagination: {
                page: 1,
                perPage: 1000,
                total: 0,
                defaultPageSize: 20,
                hideOnSinglePage: true,
                pageSizeOptions: [10, 15, 20, 50, 100],
                showQuickJumper: true,
                showSizeChanger: true,
            },
            loading: false,
            loadingInstance: null,
            checkboxOption: {
                selectedRowKeys: [],
                // row select change event
                selectedRowChange: ({ row, isSelected, selectedRowKeys }) => {
                    console.log("selectedRowChange", { row, isSelected, selectedRowKeys });
                    this.checkboxOption.selectedRowKeys = selectedRowKeys;
                },
                // selected all change event
                selectedAllChange: ({ isSelected, selectedRowKeys }) => {
                    console.log("selectedAllChange", { isSelected, selectedRowKeys });
                    this.checkboxOption.selectedRowKeys = selectedRowKeys;
                },
            },
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            filterMetas: [
                {
                    name: "options.trigger",
                    label: this.$t("message.type"),
                    materialIcon: "filter_alt",
                    controlType: "select",
                    defaultValue: null,
                    options: () => [
                        {
                            id: "timer",
                            label: this.$t("message.timer"),
                        },
                        {
                            id: "webhook",
                            label: this.$t("automation.webhook"),
                        },
                        {
                            id: "event",
                            label: this.$t("message.event"),
                        },
                        {
                            id: "button",
                            label: this.$t("message.button"),
                        },
                    ],
                },
            ],
            breakdownFilter: [
                {
                    name: "options.trigger",
                    label: this.$t("message.type"),
                    filterValue: null,
                    materialIcon: "filter_alt",
                    controlType: "select",
                    options: () => [
                        {
                            id: "timer",
                            label: this.$t("message.timer"),
                        },
                    ],
                },
            ],
            eventAutomationModalVisible: false,
            webhookAutomationFormModalVisible: false,
            tableSelectionModalVisible: false,
            selectedTableId: null,
            tableFields: [],
            selectedFieldIds: [],
            tableFieldLoading: false,
            trackFieldModalVisible: false,
            trackFieldLoading: false,
            trackAutomation: null,
            trackTableFields: [],
            trackSelectedFieldIds: [],
            messageMatchedConfigModalVisible: false,
            messageMatchedAutomation: null,
            messageMatchedConfig: {
                author: "all",
                messageType: "all",
                platform: "all",
                channels: [],
                textFilter: {
                    type: "contains",
                    value: "",
                    caseSensitive: false,
                },
            },
            preFilterModalVisible: false,
            preFilterAutomation: null,
            preFilterConfig: {
                enabled: false,
                operator: 'and',
                groups: [],
            },
            executionPrivacyModalVisible: false,
            executionPrivacyAutomation: null,
            executionPrivacyConfig: {
                users: [],
                departments: [],
            },
            showCreateCategoryModal: false,
            showEditCategoryModal: false,
            editingCategory: null,
            categoryForm: {
                name: "",
                icon: "",
            },
            categoryExpandStates: {},
            dragOverCategory: null,
            draggedAutomation: null,
            dropdownStates: {},
            categoryDropdownStates: {}, // Track category dropdown visibility states
            hasAutomationInClipboard: false,
            selectionMode: false, // Track if we're in selection mode
            permissionSettingsModalVisible: false,
            permissionSettingsAutomation: null,
            descriptionModalVisible: false,
            descriptionAutomation: null,
            descriptionForm: {
                description: "",
            },
        };
    },
    computed: {
        ...mapState({
            tables: (state) => state.table.tables,
            totalAutomationUsage: (state) => state.site.conversationStatistics.totalAutomationUsage,
            user: (state) => state.auth.user,
            adminUser: (state) => state.auth.adminUser,
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            debugMode: (state) => state.app.debugMode,
            automations: (state) => state.automation.automations,
            channels: (state) => state.channel.channels || [],
        }),
        items() {
            var self = this;
            // Vuex'teki tüm otomasyonları al
            let filteredItems = [...this.automations];

            // Read yetkisi kontrolü - sadece görüntüleme yetkisi olan otomasyonları göster
            // Süper admin ise tüm otomasyonları göster
            if (!this.adminUser) {
                filteredItems = filteredItems.filter(function(item) {
                    return canViewAutomation(item, self.user);
                });
            }

            // Type filtresi (props'tan gelen)
            if (this.type) {
                if (this.type === "button") {
                    // Buton otomasyonları: trigger === 'button' veya event type'ında "button" geçenler (örn. contact.profile_button_clicked)
                    filteredItems = filteredItems.filter(function(item) {
                        if (!item.options) return false;
                        if (item.options.trigger === "button") return true;
                        var eventType = item.options.event && item.options.event.type;
                        return eventType && eventType.indexOf("button") !== -1;
                    });
                } else {
                    filteredItems = filteredItems.filter(
                        (item) => item.options && item.options.trigger === this.type
                    );
                }
            }

            // Module filtresi (props'tan gelen)
            if (this.module) {
                filteredItems = filteredItems.filter(
                    (item) =>
                        item.options &&
                        item.options.event &&
                        item.options.event.type &&
                        item.options.event.type.startsWith(this.module)
                );
            }

            // Event filtresi (props'tan gelen)
            if (this.event) {
                filteredItems = filteredItems.filter(
                    (item) => item.options && item.options.event && item.options.event.type === this.event
                );
            }

            // Breakdown filter uygula
            const breakdownFilters = this.generateFilterParams();
            breakdownFilters.forEach((filter) => {
                if (filter.key === "options.trigger" && filter.value) {
                    filteredItems = filteredItems.filter(
                        (item) => item.options && item.options.trigger === filter.value
                    );
                }
            });

            return filteredItems;
        },
        filteredAutomations() {
            if (!this.searchText) {
                return this.items;
            }
            const searchLower = this.searchText.toLowerCase();
            return this.items.filter(
                (automation) =>
                    automation.name.toLowerCase().includes(searchLower) ||
                    (automation.options.event &&
                        (automation.options?.event?.type || "").toLowerCase().includes(searchLower))
            );
        },
        totalAutomationCount() {
            // Tüm otomasyonların sayısı (yetki filtresi uygulanmadan)
            return this.automations.length;
        },
        visibleAutomationCount() {
            // Görüntülenebilen otomasyonların sayısı (yetki filtresi uygulandıktan sonra)
            return this.items.length;
        },
        hasHiddenAutomations() {
            // Gizli otomasyon var mı?
            return this.totalAutomationCount > this.visibleAutomationCount;
        },
        categorizedAutomations() {
            const categories = {};

            // First, add all categories from automations
            this.filteredAutomations.forEach((automation) => {
                const categoryName =
                    automation.category && automation.category.name ? automation.category.name : GENERAL_CATEGORY;
                const categoryIcon = automation.category && automation.category.icon ? automation.category.icon : null;

                if (!categories[categoryName]) {
                    categories[categoryName] = {
                        name: categoryName,
                        icon: categoryIcon,
                        automations: [],
                    };
                }
                categories[categoryName].automations.push(automation);
            });

            // If search is active, don't add empty categories
            if (!this.searchText || this.searchText.trim() === "") {
                // Then, add separately created categories (even if they're empty)
                this.categories.forEach((category) => {
                    if (!categories[category.name]) {
                        categories[category.name] = {
                            name: category.name,
                            icon: category.icon,
                            automations: [],
                        };
                    } else {
                        // Update icon if category exists from automations but has different icon
                        categories[category.name].icon = category.icon;
                    }
                });

                // Always ensure default category exists when not searching
                if (!categories[GENERAL_CATEGORY]) {
                    categories[GENERAL_CATEGORY] = {
                        name: GENERAL_CATEGORY,
                        icon: null,
                        automations: [],
                    };
                }
            } else {
                // When searching, update icons for existing categories with automations
                this.categories.forEach((category) => {
                    if (categories[category.name]) {
                        categories[category.name].icon = category.icon;
                    }
                });
            }

            // Sort categories: default category always first, then alphabetically
            const sortedCategories = Object.values(categories).sort((a, b) => {
                if (a.name === GENERAL_CATEGORY) return -1;
                if (b.name === GENERAL_CATEGORY) return 1;
                return a.name.localeCompare(b.name);
            });

            return sortedCategories;
        },
    },
    methods: {
        deleteMultiAutomations() {
            const selectedCount = this.checkboxOption.selectedRowKeys.length;
            const confirmMessage = this.$t("automation.deleteAutomationsConfirmation", { count: selectedCount });

            this.$confirm({
                title: this.$t("automation.deleteAutomationsTitle"),
                content: confirmMessage,
                okText: this.$t("message.yes"),
                okType: "danger",
                cancelText: this.$t("message.cancelText"),
                onOk: () => {
                    AUTOMATION_API.deleteMultiAutomations(this.checkboxOption.selectedRowKeys)
                        .then(() => {
                            this.$message.success(this.$t("automation.automationsDeletedSuccessfully", { count: selectedCount }));
                            this.exitSelectionMode();
                            // Vuex'i güncelle
                            this.$store.dispatch("automation/syncAutomations");
                        })
                        .catch((error) => {
                            this.$message.error(this.$t("automation.automationsDeleteError"));
                            console.error("Error while deleting automations", error);
                        });
                },
            });
        },
        showEventAutomationModal() {
            this.eventAutomationModalVisible = true;
        },
        showWebhookAutomationModal() {
            this.webhookAutomationFormModalVisible = true;
        },
        async createEmptyTimerAutomation() {
            this.loading = true;
            const automation = await AUTOMATION_API.createEmptyTimerAutomation();
            this.$message.success(this.$t("automation.timerAutomationCreatedSuccessfully"));
            // Vuex sync edilecek, syncAutomations çağrısı API içinde yapılıyor
            this.gotoAutomation(automation);
            this.loading = false;
        },
        async createEmptyEventAutomation($event) {
            const { name, event, payload, eventOptions } = $event;
            this.eventAutomationModalVisible = false;

            if (event.startsWith("table")) {
                this.tableSelectionModalVisible = true;
                this.$event = $event; // Seçilen event'i component seviyesinde saklayın
                this.selectedTableId = null;
                this.tableFields = [];
                this.selectedFieldIds = [];
            } else {
                this.loading = true;
                const automation = await AUTOMATION_API.createEmptyEventAutomation(name, event, payload, null, eventOptions);
                this.$message.success(this.$t("automation.eventAutomationCreatedSuccessfully"));
                // Vuex sync edilecek, syncAutomations çağrısı API içinde yapılıyor
                this.gotoAutomation(automation);
                this.loading = false;
            }
        },
        async createWebhookAutomation($event) {
            const { name, payload } = $event;
            this.webhookAutomationFormModalVisible = false;
            this.loading = true;
            const automation = await AUTOMATION_API.createWebhookAutomation(name, payload);
            this.$message.success(this.$t("automation.webhookAutomationCreatedSuccessfully"));
            // Vuex sync edilecek, syncAutomations çağrısı API içinde yapılıyor
            this.gotoAutomation(automation);
            this.loading = false;
        },
        async fetch() {
            // Arka planda sunucudan sync et
            try {
                await this.$store.dispatch("automation/syncAutomations");
            } catch (error) {
                console.error("Error while syncing automations", error);
            }
        },
        togglePublishState(automation) {
            console.log("togglePublishState", automation);
            AUTOMATION_API.updateAutomationSingleKey(automation._id, "published", !automation.published)
                .then(() => {
                    this.$message.success(this.$t("automation.automationPublishedStateUpdatedSuccessfully"));
                    // Vuex'i güncelle
                    this.$store.dispatch("automation/syncAutomations");
                })
                .catch((error) => {
                    this.$message.error(this.$t("automation.automationPublishedStateCouldNotBeUpdated"));
                    console.error("Error while updating automation published state", error);
                });
        },
        generateFilterParams() {
            return this.breakdownFilter
                .map((filter) => {
                    if (!!filter.filterValue) {
                        return {
                            key: filter.name,
                            value: filter.filterValue,
                            operator: "equals",
                        };
                    }
                    return undefined;
                })
                .filter((x) => !!x);
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
            this.pagination.page = 1;
        },
        addFilter(name, value) {
            log.info("addFilter", name);
            const meta = this.filterMetas.find((x) => x.name === name);
            const filter = this.breakdownFilter.find((x) => x.name === name);
            if (filter) {
                this.breakdownFilter = this.breakdownFilter.map((x) => {
                    if (x.name === name) {
                        x.filterValue = value || meta.defaultValue;
                    }
                    return x;
                });
            } else {
                this.breakdownFilter.push({
                    name: meta.name,
                    label: meta.label,
                    filterValue: value || meta.defaultValue,
                    materialIcon: meta.materialIcon,
                    controlType: meta.controlType,
                    options: meta.options,
                });
            }
        },
        removeFilter(name) {
            log.info("removeFilter", name);
            this.breakdownFilter = this.breakdownFilter.filter((x) => x.name !== name);
        },
        filterLabelFormatter(name, value) {
            const filter = this.filterMetas.find((x) => x.name === name);
            log.info("filterLabelFormatter", { name, value, filter });
            const isSelect = filter.controlType === "select";
            if (isSelect) {
                const option = filter.options().find((x) => x.id === value);
                if (option) {
                    return `${filter.label}: ${option.label}`;
                }
            }
            if (value) {
                return `${filter.label}: ${value}`;
            }
            return filter.label;
        },
        isFilterExists(name) {
            return !!this.breakdownFilter.find((x) => x.name === name);
        },
        gotoAutomation(row) {
            window.sessionStorage.setItem("automation-selected-module", this.module);
            this.$router.push(`/automations/${row._id}/design`);
        },
        getAutomationHref(automation) {
            return `/automations/${automation._id}/design`;
        },
        handleAutomationClick(automation, event) {
            // Selection mode'da ise normal tıklama davranışını engelle
            if (this.selectionMode) {
                event.preventDefault();
                return;
            }

            // Command/Ctrl + Click durumunda tarayıcının varsayılan davranışına izin ver
            if (event.metaKey || event.ctrlKey) {
                // sessionStorage'ı ayarla çünkü yeni sekmede açılacak
                window.sessionStorage.setItem("automation-selected-module", this.module);
                return; // Tarayıcının varsayılan davranışına izin ver
            } else {
                // Normal tıklama durumunda varsayılan davranışı engelle ve router kullan
                event.preventDefault();
                this.gotoAutomation(automation);
            }
        },
        async createEventAutomationWithTable() {
            const { name, event, payload } = this.$event;
            this.tableSelectionModalVisible = false;
            this.loading = true;
            if (event === 'table.record_updated') {
                if (!this.selectedFieldIds || this.selectedFieldIds.length === 0) {
                    this.$message.error(this.$t('automation.selectAtLeastOneField'));
                    this.loading = false;
                    return;
                }
            }
            const automation = await AUTOMATION_API.createEmptyEventAutomation(
                name,
                event,
                payload,
                {
                    table: this.selectedTableId,
                },
                event === 'table.record_updated' ? { trackFieldIds: this.selectedFieldIds } : undefined
            );
            this.$message.success(this.$t("automation.eventAutomationCreatedSuccessfully"));
            // Vuex sync edilecek, syncAutomations çağrısı API içinde yapılıyor
            this.gotoAutomation(automation);
            this.loading = false;
        },
        getMaxWidth() {
            return window.innerWidth;
        },
        toggleCategory(categoryName) {
            this.categoryExpandStates[categoryName] = !this.categoryExpandStates[categoryName];
        },
        async fetchTableFields(tableId) {
            try {
                this.tableFieldLoading = true;
                const table = this.tables.find((t) => t._id === tableId);
                // Vuex'ten gelen tables içinde fields yoksa API'den çek
                if (table && Array.isArray(table.fields) && table.fields.length > 0) {
                    this.tableFields = table.fields.filter((f) => !f.fieldType.startsWith('record_'));
                } else {
                    const { tables } = await this.$store.dispatch('table/fetchCustomerTables');
                    const t = tables.find((x) => x._id === tableId) || {};
                    const fields = Array.isArray(t.fields) ? t.fields : [];
                    this.tableFields = fields.filter((f) => !(f.fieldType || '').startsWith('record_'));
                }
            } catch (e) {
                this.tableFields = [];
            } finally {
                this.tableFieldLoading = false;
            }
        },
        editCategory(category) {
            this.editingCategory = { ...category };
            this.showEditCategoryModal = true;
            this.categoryForm.name = category.name;
            this.categoryForm.icon = category.icon;
        },
        closeCategoryModal() {
            this.showCreateCategoryModal = false;
            this.showEditCategoryModal = false;
            this.editingCategory = null;
            this.categoryForm = {
                name: "",
                icon: "",
            };
        },
        async saveCategory() {
            try {
                if (!this.categoryForm.name.trim()) {
                    this.$message.error(this.$t("automation.categoryNameRequired"));
                    return;
                }

                // Check if category name already exists (except for current editing category)
                const existingInCategories = this.categories.find(
                    (cat) =>
                        cat.name === this.categoryForm.name &&
                        (!this.editingCategory || cat.name !== this.editingCategory.name)
                );

                const existingInAutomations = this.categorizedAutomations.find(
                    (cat) =>
                        cat.name === this.categoryForm.name &&
                        (!this.editingCategory || cat.name !== this.editingCategory.name)
                );

                if (existingInCategories || existingInAutomations) {
                    this.$message.error(this.$t("automation.categoryNameAlreadyExists"));
                    return;
                }

                if (this.editingCategory) {
                    // Update existing category
                    await this.updateCategoryName(
                        this.editingCategory.name,
                        this.categoryForm.name,
                        this.categoryForm.icon
                    );

                    // Update in categories array
                    const categoryIndex = this.categories.findIndex((cat) => cat.name === this.editingCategory.name);
                    if (categoryIndex !== -1) {
                        this.$set(this.categories, categoryIndex, {
                            name: this.categoryForm.name,
                            icon: this.categoryForm.icon,
                        });
                    }
                } else {
                    // Create new category - add to categories array
                    this.categories.push({
                        name: this.categoryForm.name,
                        icon: this.categoryForm.icon,
                    });
                    this.$message.success(this.$t("automation.categoryCreatedSuccessfully"));
                }

                this.closeCategoryModal();
            } catch (error) {
                console.error("Error saving category:", error);
                this.$message.error(this.$t("automation.categorySaveError"));
            }
        },
        async updateCategoryName(oldName, newName, newIcon) {
            try {
                // Find all automations with this category and update them
                const automationsToUpdate = this.items.filter(
                    (automation) => automation.category && automation.category.name === oldName
                );

                for (const automation of automationsToUpdate) {
                    await AUTOMATION_API.updateAutomationSingleKey(automation._id, "category", {
                        name: newName,
                        icon: newIcon,
                    });
                }

                // Vuex'i güncelle
                await this.$store.dispatch("automation/syncAutomations");

                this.$message.success(this.$t("automation.categoryUpdatedSuccessfully"));
            } catch (error) {
                console.error("Error updating category:", error);
                this.$message.error(this.$t("automation.categoryUpdateError"));
            }
        },
        async deleteCategory(categoryName) {
            try {
                await this.$confirm({
                    title: this.$t("automation.deleteCategory"),
                    content: this.$t("automation.deleteCategoryConfirmation", { categoryName, generalCategory: this.$t("automation.generalCategory") }),
                    okText: this.$t("message.yes"),
                    okType: "danger",
                    cancelText: this.$t("message.no"),
                    onOk: async () => {
                        // Find all automations with this category and move them to General
                        const automationsToUpdate = this.items.filter(
                            (automation) => automation.category && automation.category.name === categoryName
                        );

                        for (const automation of automationsToUpdate) {
                            await AUTOMATION_API.updateAutomationSingleKey(automation._id, "category", {
                                name: null,
                                icon: null,
                            });
                        }

                        // Remove from categories array
                        this.categories = this.categories.filter((cat) => cat.name !== categoryName);

                        // Vuex'i güncelle
                        await this.$store.dispatch("automation/syncAutomations");

                        this.$message.success(this.$t("automation.categoryDeletedSuccessfully"));
                    },
                });
            } catch (error) {
                console.error("Error deleting category:", error);
                this.$message.error(this.$t("automation.categoryDeleteError"));
            }
        },
        onDragStart(event, automation) {
            this.draggedAutomation = automation;
            event.dataTransfer.effectAllowed = "move";
        },
        allowDrop(event, categoryName) {
            event.preventDefault();
            this.dragOverCategory = categoryName;
            event.dataTransfer.dropEffect = "move";
        },
        onDragLeave(event) {
            this.dragOverCategory = null;
        },
        onDragEnd(event) {
            this.dragOverCategory = null;
        },
        async onDrop(event, categoryName) {
            event.preventDefault();

            if (!this.draggedAutomation || !this.draggedAutomation._id) {
                this.draggedAutomation = null;
                this.dragOverCategory = null;
                return;
            }

            try {
                const displayCategoryName = categoryName === GENERAL_CATEGORY ? this.$t("automation.generalCategory") : categoryName;
                const newCategory =
                    categoryName === GENERAL_CATEGORY
                        ? { name: null, icon: null }
                        : { name: categoryName, icon: this.getCategoryIcon(categoryName) };

                await AUTOMATION_API.updateAutomationSingleKey(this.draggedAutomation._id, "category", newCategory);

                // Vuex'i güncelle
                await this.$store.dispatch("automation/syncAutomations");

                this.$message.success(this.$t("automation.automationMovedToCategory", { categoryName: displayCategoryName }));
            } catch (error) {
                console.error("Error moving automation:", error);
                this.$message.error(this.$t("automation.automationMoveError"));
            } finally {
                this.draggedAutomation = null;
                this.dragOverCategory = null;
            }
        },
        getCategoryIcon(categoryName) {
            // First check in separately created categories
            const separateCategory = this.categories.find((cat) => cat.name === categoryName);
            if (separateCategory) {
                return separateCategory.icon;
            }

            // Fallback to categorized automations
            const category = this.categorizedAutomations.find((cat) => cat.name === categoryName);
            return category ? category.icon : null;
        },
        async duplicateAutomation(automation) {
            try {
                // Close dropdown
                this.$set(this.dropdownStates, automation._id, false);

                const response = await AUTOMATION_API.duplicateAutomation(automation._id);
                // Vuex'i güncelle
                await this.$store.dispatch("automation/syncAutomations");
                this.$message.success(this.$t("automation.automationCopied"));
            } catch (error) {
                console.error("Error duplicating automation:", error);
                this.$message.error(this.$t("automation.automationCopyError"));
            }
        },
        async copyAutomationAsJSON(automation) {
            try {
                // Close dropdown
                this.$set(this.dropdownStates, automation._id, false);

                const exportData = await AUTOMATION_API.exportAutomationAsJSON(automation._id);

                // Clipboard'a kopyala
                if (navigator.clipboard) {
                    await navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
                    this.$message.success(
                        this.$t("automation.automationCopiedToClipboard")
                    );
                } else {
                    // Fallback: Create textarea and copy
                    const textArea = document.createElement("textarea");
                    textArea.value = JSON.stringify(exportData, null, 2);
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand("copy");
                    document.body.removeChild(textArea);
                    this.$message.success(this.$t("automation.automationCopiedToClipboard"));
                }

                // Clipboard kontrolü yap
                setTimeout(() => {
                    this.checkClipboard();
                }, 200);
            } catch (error) {
                console.error("Error copying automation as JSON:", error);
                this.$message.error(this.$t("automation.automationJSONCopyError"));
            }
        },
        async pasteAutomation() {
            try {
                let clipboardText;

                // Clipboard'dan text al
                if (navigator.clipboard) {
                    clipboardText = await navigator.clipboard.readText();
                } else {
                    this.$message.warning(this.$t("automation.clipboardNotSupported"));
                    return;
                }

                // JSON parse et
                let importData;
                try {
                    importData = JSON.parse(clipboardText);
                } catch (parseError) {
                    this.$message.warning(this.$t("automation.invalidAutomationJSON"));
                    return;
                }

                // Supsis otomasyon export'u mu kontrol et
                if (!importData.supsis_automation_export || importData.version !== "1.0") {
                    this.$message.warning(this.$t("automation.invalidAutomationExport"));
                    return;
                }

                const response = await AUTOMATION_API.importAutomationFromJSON(importData);
                // Vuex'i güncelle
                await this.$store.dispatch("automation/syncAutomations");

                this.$message.success(this.$t("automation.automationImportedSuccessfully", { name: response.name }));

                // Clipboard'ı tekrar kontrol et
                setTimeout(() => {
                    this.checkClipboard();
                }, 1000);
            } catch (error) {
                console.error("Error pasting automation:", error);
                this.$message.error(this.$t("automation.automationPasteError"));
            }
        },
        handleKeyDown(event) {
            // Ctrl+V veya Cmd+V (Mac)
            if ((event.ctrlKey || event.metaKey) && event.key === "v") {
                // Input alanlarında değilse paste yap
                const activeElement = document.activeElement;
                const isInputElement =
                    activeElement &&
                    (activeElement.tagName === "INPUT" ||
                        activeElement.tagName === "TEXTAREA" ||
                        activeElement.contentEditable === "true");

                if (!isInputElement) {
                    event.preventDefault();
                    this.pasteAutomation();
                }
            }
        },

        async checkClipboard() {
            try {
                if (!navigator.clipboard) {
                    this.hasAutomationInClipboard = false;
                    return;
                }

                const clipboardText = await navigator.clipboard.readText();

                // JSON parse et
                let importData;
                try {
                    importData = JSON.parse(clipboardText);
                } catch (parseError) {
                    this.hasAutomationInClipboard = false;
                    return;
                }

                // Supsis otomasyon export'u mu kontrol et
                if (importData.supsis_automation_export && importData.version === "1.0") {
                    this.hasAutomationInClipboard = true;
                } else {
                    this.hasAutomationInClipboard = false;
                }
            } catch (error) {
                // Permission denied veya başka bir hata
                this.hasAutomationInClipboard = false;
            }
        },

        handleWindowFocus() {
            // Kullanıcı başka yerden geldiğinde clipboard'ı kontrol et
            this.checkClipboard();
        },

        availableCategoriesForAutomation(automation) {
            const currentCategory =
                automation.category && automation.category.name ? automation.category.name : GENERAL_CATEGORY;

            return this.categorizedAutomations.filter((category) => category.name !== currentCategory);
        },

        async moveAutomationToCategory(automation, categoryName) {
            try {
                // Close dropdown
                this.$set(this.dropdownStates, automation._id, false);

                const displayCategoryName = categoryName === GENERAL_CATEGORY ? this.$t("automation.generalCategory") : categoryName;
                const newCategory =
                    categoryName === GENERAL_CATEGORY
                        ? { name: null, icon: null }
                        : { name: categoryName, icon: this.getCategoryIcon(categoryName) };

                await AUTOMATION_API.updateAutomationSingleKey(automation._id, "category", newCategory);

                // Vuex'i güncelle
                await this.$store.dispatch("automation/syncAutomations");

                this.$message.success(this.$t("automation.automationMovedToCategory", { categoryName: displayCategoryName }));
            } catch (error) {
                console.error("Error moving automation:", error);
                this.$message.error(this.$t("automation.automationMoveError"));
            }
        },

        async openTrackFieldsModal(automation) {
            try {
                // menüyü kapat
                this.$set(this.dropdownStates, automation._id, false);
                this.trackAutomation = automation;
                this.trackFieldModalVisible = true;
                this.trackFieldLoading = true;

                const tableId = (automation.reference && automation.reference.table) ? automation.reference.table : null;
                if (!tableId) {
                    this.$message.error(this.$t('automation.tableMustBeSelectedFirst'));
                    this.closeTrackFieldsModal();
                    return;
                }

                // Tablo alanlarını yükle (Vuex içinden veya gerekirse fetch)
                const table = this.tables.find((t) => t._id === tableId);
                if (table && Array.isArray(table.fields) && table.fields.length > 0) {
                    this.trackTableFields = table.fields.filter((f) => !(f.fieldType || '').startsWith('record_'));
                } else {
                    const { tables } = await this.$store.dispatch('table/fetchCustomerTables');
                    const t = tables.find((x) => x._id === tableId) || {};
                    const fields = Array.isArray(t.fields) ? t.fields : [];
                    this.trackTableFields = fields.filter((f) => !(f.fieldType || '').startsWith('record_'));
                }

                // mevcut seçili alanları doldur
                const current = (automation.options && automation.options.eventOptions && automation.options.eventOptions.trackFieldIds) || [];
                this.trackSelectedFieldIds = current.slice();
            } catch (e) {
                this.$message.error(this.$t('automation.fieldsLoadError'));
                this.closeTrackFieldsModal();
            } finally {
                this.trackFieldLoading = false;
            }
        },

        closeTrackFieldsModal() {
            this.trackFieldModalVisible = false;
            this.trackAutomation = null;
            this.trackTableFields = [];
            this.trackSelectedFieldIds = [];
        },

        async saveTrackFieldsForAutomation() {
            try {
                if (!this.trackAutomation) return;
                // boş seçim geriye dönük uyumluluk için izinli: boş gönderilirse tüm alanlar çalışır
                await AUTOMATION_API.updateAutomationSingleKey(
                    this.trackAutomation._id,
                    'options.eventOptions',
                    { trackFieldIds: this.trackSelectedFieldIds }
                );

                // Vuex'i güncelle
                await this.$store.dispatch("automation/syncAutomations");

                this.$message.success(this.$t('automation.trackFieldsUpdated'));
                this.closeTrackFieldsModal();
            } catch (e) {
                this.$message.error(this.$t('automation.updateError'));
            }
        },

        availableCategoriesForCategory(currentCategoryName) {
            return this.categorizedAutomations.filter((category) => category.name !== currentCategoryName);
        },

        async moveAllAutomationsToCategory(fromCategoryName, toCategoryName) {
            try {
                // Close dropdown
                this.$set(this.categoryDropdownStates, fromCategoryName, false);

                const sourceCategory = this.categorizedAutomations.find((cat) => cat.name === fromCategoryName);
                if (!sourceCategory || sourceCategory.automations.length === 0) {
                    this.$message.warning(this.$t("automation.noAutomationToMove"));
                    return;
                }

                const displayCategoryName = toCategoryName === GENERAL_CATEGORY ? this.$t("automation.generalCategory") : toCategoryName;
                const newCategory =
                    toCategoryName === GENERAL_CATEGORY
                        ? { name: null, icon: null }
                        : { name: toCategoryName, icon: this.getCategoryIcon(toCategoryName) };

                // Update all automations in the category
                for (const automation of sourceCategory.automations) {
                    await AUTOMATION_API.updateAutomationSingleKey(automation._id, "category", newCategory);
                }

                // Vuex'i güncelle
                await this.$store.dispatch("automation/syncAutomations");

                this.$message.success(
                    this.$t("automation.automationsMovedToCategory", { count: sourceCategory.automations.length, categoryName: displayCategoryName })
                );
            } catch (error) {
                console.error("Error moving all automations:", error);
                this.$message.error(this.$t("automation.automationsMoveError"));
            }
        },

        enterSelectionMode(automation) {
            // Close dropdown
            this.$set(this.dropdownStates, automation._id, false);

            // Enter selection mode and select the clicked automation
            this.selectionMode = true;
            this.checkboxOption.selectedRowKeys = [automation._id];
        },

        exitSelectionMode() {
            this.selectionMode = false;
            this.checkboxOption.selectedRowKeys = [];
        },

        toggleAutomationSelection(automationId) {
            const isSelected = this.checkboxOption.selectedRowKeys.includes(automationId);

            if (isSelected) {
                this.checkboxOption.selectedRowKeys = this.checkboxOption.selectedRowKeys.filter(
                    (id) => id !== automationId
                );
            } else {
                this.checkboxOption.selectedRowKeys.push(automationId);
            }
        },

        async makeFreeTier(automation) {
            try {
                // Close dropdown
                this.$set(this.dropdownStates, automation._id, false);

                // Şifre sor
                const password = await this.promptForPassword();
                if (!password) {
                    return;
                }

                // API çağrısı yap
                await AUTOMATION_API.updateBillableStatus(automation._id, false, password);

                // Vuex'i güncelle
                await this.$store.dispatch("automation/syncAutomations");

                this.$message.success(this.$t('automation.automationMadeFreeTier'));
            } catch (error) {
                console.error('Error making automation free tier:', error);
                if (error.response && error.response.status === 403) {
                    this.$message.error(this.$t('automation.wrongPassword'));
                } else {
                    this.$message.error(this.$t('automation.makeFreeTierError'));
                }
            }
        },

        async revertFreeTier(automation) {
            try {
                // Close dropdown
                this.$set(this.dropdownStates, automation._id, false);

                // API çağrısı yap (şifre yok)
                await AUTOMATION_API.updateBillableStatus(automation._id, true);

                // Vuex'i güncelle
                await this.$store.dispatch("automation/syncAutomations");

                this.$message.success(this.$t('automation.automationRevertedToNormalTier'));
            } catch (error) {
                console.error('Error reverting automation free tier:', error);
                this.$message.error(this.$t('automation.revertFreeTierError'));
            }
        },

        promptForPassword() {
            const password = window.prompt(this.$t('automation.freeTierPasswordPrompt'));
            return Promise.resolve(password);
        },

        async openMessageMatchedConfigModal(automation) {
            try {
                // menüyü kapat
                this.$set(this.dropdownStates, automation._id, false);
                this.messageMatchedAutomation = automation;
                this.messageMatchedConfigModalVisible = true;

                // Mevcut config'i yükle
                const currentConfig = (automation.options && automation.options.eventOptions) || {};
                this.messageMatchedConfig = {
                    author: currentConfig.author || "all",
                    messageType: currentConfig.messageType || "all",
                    platform: currentConfig.platform || "all",
                    channels: currentConfig.channels || [],
                    textFilter: {
                        type: (currentConfig.textFilter && currentConfig.textFilter.type) || "contains",
                        value: (currentConfig.textFilter && currentConfig.textFilter.value) || "",
                        caseSensitive: (currentConfig.textFilter && currentConfig.textFilter.caseSensitive) || false,
                    },
                };
            } catch (e) {
                this.$message.error(this.$t('automation.configLoadError'));
                this.closeMessageMatchedConfigModal();
            }
        },

        closeMessageMatchedConfigModal() {
            this.messageMatchedConfigModalVisible = false;
            this.messageMatchedAutomation = null;
            // Reset config
            this.messageMatchedConfig = {
                author: "all",
                messageType: "all",
                platform: "all",
                channels: [],
                textFilter: {
                    type: "contains",
                    value: "",
                    caseSensitive: false,
                },
            };
        },

        async saveMessageMatchedConfig(config) {
            try {
                if (!this.messageMatchedAutomation) return;

                await AUTOMATION_API.updateAutomationSingleKey(
                    this.messageMatchedAutomation._id,
                    'options.eventOptions',
                    config
                );

                // Vuex'i güncelle
                await this.$store.dispatch("automation/syncAutomations");

                this.$message.success(this.$t('automation.messageFilterConfigUpdated'));
                this.closeMessageMatchedConfigModal();
            } catch (e) {
                this.$message.error(this.$t('automation.updateError'));
            }
        },

        isPreFilterSupported(automation) {
            const eventType = _.get(automation, 'options.event.type', '');
            return eventType.startsWith('chat.') || eventType.startsWith('contact.');
        },

        openPreFilterModal(automation) {
            this.$set(this.dropdownStates, automation._id, false);
            this.preFilterAutomation = automation;
            this.preFilterModalVisible = true;
            
            const current = _.get(automation, 'preFilters', {
                enabled: false,
                operator: 'and',
                groups: [],
            });
            this.preFilterConfig = _.cloneDeep(current);
        },

        closePreFilterModal() {
            this.preFilterModalVisible = false;
            this.preFilterAutomation = null;
            this.preFilterConfig = {
                enabled: false,
                operator: 'and',
                groups: [],
            };
        },

        async savePreFilterConfig() {
            try {
                if (!this.preFilterAutomation) return;

                await AUTOMATION_API.updateAutomationSingleKey(
                    this.preFilterAutomation._id,
                    'preFilters',
                    this.preFilterConfig
                );

                await this.$store.dispatch("automation/syncAutomations");
                this.$message.success(this.$t('automation.preFiltersSaved'));
                this.closePreFilterModal();
            } catch (e) {
                this.$message.error(this.$t('automation.preFiltersSaveError'));
            }
        },

        isExecutionPrivacySupported(automation) {
            const eventType = _.get(automation, 'options.event.type', '');
            return eventType === 'contact.profile_button_clicked' || eventType === 'message.picked';
        },

        openExecutionPrivacyModal(automation) {
            this.$set(this.dropdownStates, automation._id, false);
            this.executionPrivacyAutomation = automation;
            this.executionPrivacyModalVisible = true;
            
            const current = _.get(automation, 'executionPrivacy', {
                users: [],
                departments: [],
            });
            this.executionPrivacyConfig = _.cloneDeep(current);
        },

        closeExecutionPrivacyModal() {
            this.executionPrivacyModalVisible = false;
            this.executionPrivacyAutomation = null;
            this.executionPrivacyConfig = {
                users: [],
                departments: [],
            };
        },

        async saveExecutionPrivacyConfig() {
            try {
                if (!this.executionPrivacyAutomation) return;

                await AUTOMATION_API.updateAutomationSingleKey(
                    this.executionPrivacyAutomation._id,
                    'executionPrivacy',
                    this.executionPrivacyConfig
                );

                await this.$store.dispatch("automation/syncAutomations");
                this.$message.success(this.$t('automation.executionPrivacySaved'));
                this.closeExecutionPrivacyModal();
            } catch (e) {
                this.$message.error(this.$t('automation.executionPrivacySaveError'));
            }
        },

        openPermissionSettingsModal(automation) {
            this.$set(this.dropdownStates, automation._id, false);
            this.permissionSettingsAutomation = automation;
            this.permissionSettingsModalVisible = true;
        },

        closePermissionSettingsModal() {
            this.permissionSettingsModalVisible = false;
            this.permissionSettingsAutomation = null;
        },

        onPermissionConfigSaved(permissionConfig) {
            // Lokaldeki otomasyon objesini güncelle
            if (this.permissionSettingsAutomation) {
                this.$set(this.permissionSettingsAutomation, "permissionConfig", permissionConfig);
                
                // Automations listesindeki otomasyon objesini de güncelle
                var automationId = this.permissionSettingsAutomation._id;
                var automationIndex = this.automations.findIndex(function(a) {
                    return a._id === automationId;
                });
                if (automationIndex !== -1) {
                    this.$set(this.automations[automationIndex], "permissionConfig", permissionConfig);
                }
            }
        },

        openDescriptionModal(automation) {
            this.$set(this.dropdownStates, automation._id, false);
            this.descriptionAutomation = automation;
            this.descriptionForm.description = automation.description || "";
            this.descriptionModalVisible = true;
        },

        closeDescriptionModal() {
            this.descriptionModalVisible = false;
            this.descriptionAutomation = null;
            this.descriptionForm.description = "";
        },

        async saveDescription() {
            try {
                if (!this.descriptionAutomation) return;

                await AUTOMATION_API.updateAutomationSingleKey(
                    this.descriptionAutomation._id,
                    'description',
                    this.descriptionForm.description
                );

                await this.$store.dispatch("automation/syncAutomations");
                this.$message.success(this.$t('automation.descriptionSaved'));
                this.closeDescriptionModal();
            } catch (e) {
                this.$message.error(this.$t('automation.descriptionSaveError'));
            }
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".table-wrapper"),
            name: "wave",
        });
        // Arka planda sync yap ama loading gösterme
        this.fetch();

        // Keyboard event listener ekle
        document.addEventListener("keydown", this.handleKeyDown);

        // Window focus event listener ekle
        window.addEventListener("focus", this.handleWindowFocus);

        // İlk clipboard kontrolü yap (biraz gecikme ile)
        setTimeout(() => {
            this.checkClipboard();
        }, 500);
    },
    destroyed() {
        // Event listener'ları temizle
        document.removeEventListener("keydown", this.handleKeyDown);
        window.removeEventListener("focus", this.handleWindowFocus);
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
        channelId(val) {
            this.searchText = "";
            this.pagination.page = 1;
        },
        selectedTableId(val) {
            if (val) {
                this.fetchTableFields(val);
            } else {
                this.tableFields = [];
                this.selectedFieldIds = [];
            }
        },
        searchText(newVal, oldVal) {
            // Reset pagination when search text changes
            if (newVal !== oldVal) {
                this.pagination.page = 1;
            }
        },
        categorizedAutomations: {
            handler(newCategories) {
                if (newCategories && Array.isArray(newCategories)) {
                    // Ensure all categories start expanded by default
                    newCategories.forEach((category) => {
                        if (this.categoryExpandStates[category.name] === undefined) {
                            this.$set(this.categoryExpandStates, category.name, true);
                        }
                    });
                }
            },
            immediate: true,
        },
    },
});
</script>

<style scoped lang="scss">
.page-automation-list {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 10px;

    .title {
        display: flex;
        align-items: center;
        gap: 6px;

        span {
            background: var(--color-bg-neutral-default);
            border: 1px solid var(--color-border-default);
            padding: 4px;
            border-radius: 4px;
            font-size: 14px;
        }
    }

    .paste-info-message {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        background: linear-gradient(135deg, #e6f7ff, #bae7ff);
        border: 1px solid #40a9ff;
        border-radius: 8px;
        color: #1890ff;
        font-size: 14px;
        margin-bottom: 10px;
        box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
        animation: slideInDown 0.3s ease-out;

        .material-icons {
            font-size: 20px;
            color: #40a9ff;
        }

        strong {
            color: #0050b3;
            font-weight: 600;
        }
    }

    @keyframes slideInDown {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .table-wrapper {
        display: flex;
        flex-direction: column;
        gap: 5px;

        .toolbar {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            gap: 5px;

            .left {
                display: flex;
                gap: 5px;
                align-items: center;
            }

            .right {
                display: flex;
                gap: 5px;

                .category-btn {
                    .material-icons {
                        margin-right: 5px;
                    }
                }
            }

            .common-filter-tool {
                border-bottom: none !important;
                padding: unset !important;
            }
        }

        .selection-toolbar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 15px 20px;
            background: #f0f8ff;
            border: 1px solid #1890ff;
            border-radius: 8px;
            margin-bottom: 10px;

            .selection-info {
                display: flex;
                align-items: center;
                gap: 15px;
                color: #1890ff;
                font-weight: 500;
            }
        }

        .categories-container {
            display: flex;
            flex-direction: column;
            gap: 20px;

            .category-section {
                border: 1px solid #e8e8e8;
                border-radius: 8px;
                padding: 0;
                background: #fafafa;
                transition: all 0.3s ease;

                &:hover {
                    border-color: #d9d9d9;
                }

                &.drag-over {
                    border-color: #1890ff !important;
                    background-color: #f0f8ff !important;
                }

                .category-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 15px 20px;
                    background: #f5f5f5;
                    border-bottom: 1px solid #e8e8e8;
                    border-radius: 8px 8px 0 0;
                    cursor: pointer;
                    transition: background-color 0.2s ease;

                    &:hover {
                        background: #eeeeee;
                    }

                    .category-info {
                        display: flex;
                        align-items: center;
                        gap: 10px;

                        .collapse-toggle {
                            padding: 4px !important;
                            min-width: 32px !important;
                            height: 32px !important;

                            .material-icons {
                                font-size: 20px;
                                transition: transform 0.3s ease;
                            }

                            &:hover .material-icons {
                                transform: scale(1.1);
                            }
                        }

                        .category-icon {
                            font-size: 24px;
                            color: #666;
                        }

                        .category-name {
                            font-size: 18px;
                            font-weight: 600;
                            color: #333;
                        }

                        .automation-count {
                            font-size: 14px;
                            color: #999;
                        }
                    }

                    .category-actions {
                        display: flex;
                        gap: 5px;
                        align-items: center;

                        .category-more-actions-btn {
                            opacity: 0.7;
                            transition: opacity 0.2s ease;

                            &:hover {
                                opacity: 1;
                            }
                        }
                    }
                }

                .automations-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
                    gap: 15px;
                    padding: 20px;
                    transition: all 0.3s ease;
                    overflow: hidden;

                    &.collapsed {
                        max-height: 0;
                        padding: 0 20px;
                        opacity: 0;
                    }

                    .empty-category-message {
                        grid-column: 1 / -1;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 10px;
                        padding: 40px 20px;
                        color: #999;
                        font-style: italic;
                        border: 2px dashed #e8e8e8;
                        border-radius: 8px;
                        background: #fafafa;

                        .material-icons {
                            font-size: 20px;
                        }
                    }

                    .automation-card {
                        background: white;
                        border: 1px solid #e8e8e8;
                        border-radius: 8px;
                        padding: 15px;
                        cursor: grab;
                        transition: all 0.3s ease;

                        &:hover {
                            border-color: #1890ff;
                            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                        }

                        &:active {
                            cursor: grabbing;
                        }

                        &.selection-mode {
                            cursor: pointer;

                            &:hover {
                                background: #f5f5f5;
                            }

                            &.selected {
                                border-color: #1890ff;
                                background: #f0f8ff;
                                box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
                            }
                        }

                        .automation-card-header {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-start;
                            margin-bottom: 15px;

                            .automation-selection {
                                margin-right: 10px;
                                padding-top: 2px;

                                .ant-checkbox {
                                    transform: scale(1.3);
                                }

                                .ant-checkbox-wrapper {
                                    margin-right: 0;
                                }
                            }

                            .automation-name {
                                margin: 0;
                                font-size: 16px;
                                font-weight: 600;
                                color: #1890ff;
                                cursor: pointer;
                                flex: 1;
                                text-decoration: none;
                                display: block;

                                &:hover {
                                    text-decoration: underline;
                                }
                            }

                            .automation-actions {
                                display: flex;
                                gap: 5px;
                                margin-left: 10px;
                            }
                        }

                        .automation-trigger {
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            margin-bottom: 15px;
                            padding: 8px 12px;
                            background: #f5f5f5;
                            border-radius: 6px;
                            font-size: 14px;
                            color: #666;

                            .material-icons {
                                font-size: 18px;
                            }
                        }

                        .automation-description {
                            display: flex;
                            align-items: flex-start;
                            gap: 8px;
                            margin-bottom: 15px;
                            padding: 8px 12px;
                            background: #e8f4fd;
                            border-radius: 6px;
                            font-size: 13px;
                            color: #555;
                            max-height: 60px;
                            overflow: hidden;

                            .material-icons {
                                font-size: 16px;
                                flex-shrink: 0;
                                color: #1890ff;
                            }

                            span {
                                overflow: hidden;
                                text-overflow: ellipsis;
                                display: -webkit-box;
                                -webkit-line-clamp: 2;
                                -webkit-box-orient: vertical;
                            }
                        }

                        .automation-meta {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            font-size: 12px;
                            color: #999;

                            .meta-left {
                                display: flex;
                                align-items: center;
                                gap: 15px;

                                .created-by {
                                    display: flex;
                                    align-items: center;
                                    gap: 5px;

                                    span {
                                        max-width: 100px;
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                        white-space: nowrap;
                                    }
                                }
                            }

                            .meta-right {
                                display: flex;
                                align-items: center;

                                .publish-switch {
                                    font-size: 12px;

                                    .switch-label {
                                        font-size: 10px;
                                        margin-left: 5px;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    .free-tier-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        background: linear-gradient(135deg, #ffd700, #ffed4e);
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        color: #b8860b;
        margin-top: 10px;
        box-shadow: 0 2px 4px rgba(255, 215, 0, 0.3);

        .material-icons {
            font-size: 16px;
            color: #b8860b;
        }
    }

    .agent-detail {
        display: flex;
        gap: 5px;
    }

    .automation-name-link {
        color: inherit;
        text-decoration: none;
        cursor: pointer;
        color: inherit;

        &:hover {
            text-decoration: underline !important;
        }
    }
}
</style>

<style lang="scss">
.page-automation-list {
    overflow-x: hidden;

    .ant-modal {
        width: 75vw !important;
    }

    .custom-modal-body {
        background: var(--color-bg-default) !important;
    }
    // Drag and drop styling
    .category-section {
        &.drag-over {
            border-color: #1890ff !important;
            background-color: #f0f8ff !important;
        }
    }

    .automation-card {
        &.dragging {
            opacity: 0.5;
            transform: rotate(5deg);
        }
    }
}

.create-event-automation-modal {
    .ant-modal {
        width: 70vw !important;
    }
}

.table-selection-form {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .event-description {
        font-size: 16px;
        font-weight: 500;
        color: var(--color-text-strong-default);
    }
}

.prefilter-config-modal {
    .ant-modal {
        width: 950px !important;
        max-width: 90vw !important;
    }
}

.execution-privacy-config-modal {
    .ant-modal {
        width: 650px !important;
        max-width: 90vw !important;
    }
}

.automation-dropdown-menu {
    .ant-dropdown-menu-item {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 15px;
    }
}

.automation-category-form {
    padding: 20px;

    .form-group {
        margin-bottom: 20px;

        label {
            display: block;
            margin-bottom: 8px;
            font-weight: 600;
            color: #333;
        }
    }

    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 30px;
    }
}

.automation-actions-dropdown,
.category-actions-dropdown {
    .ant-popover-inner-content {
        padding: 0 !important;
    }

    .dropdown-menu-content {
        min-width: 200px;

        .dropdown-menu-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 16px;
            cursor: pointer;
            transition: background-color 0.2s ease;
            border-bottom: 1px solid #f0f0f0;
            position: relative;

            &:last-child {
                border-bottom: none;
            }

            &:hover {
                background-color: #f5f5f5;
            }

            .material-icons {
                font-size: 18px;
                color: #666;
            }

            span:not(.submenu-arrow):not(.material-icons) {
                color: #333;
                font-size: 14px;
                flex: 1;
            }

            &.submenu-item {
                position: relative;

                .submenu-arrow {
                    margin-left: auto;
                    font-size: 16px;
                    color: #999;
                }

                .submenu-content {
                    position: absolute;
                    right: 100%;
                    top: 0;
                    min-width: 180px;
                    background: white;
                    border: 1px solid #e8e8e8;
                    border-radius: 6px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                    opacity: 0;
                    visibility: hidden;
                    transform: translateX(10px);
                    transition: all 0.2s ease;
                    z-index: 1000;

                    .submenu-item-content {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 12px 16px;
                        cursor: pointer;
                        transition: background-color 0.2s ease;
                        border-bottom: 1px solid #f0f0f0;

                        &:last-child {
                            border-bottom: none;
                        }

                        &:hover {
                            background-color: #f5f5f5;
                        }

                        .material-icons {
                            font-size: 16px;
                            color: #666;
                        }

                        span {
                            color: #333;
                            font-size: 14px;
                        }
                    }
                }

                &:hover .submenu-content {
                    opacity: 1;
                    visibility: visible;
                    transform: translateX(0);
                }
            }
        }
    }
}
</style>
